import React from "react";
import { Settings, ChevronDown, AlertCircle } from "lucide-react";
import PollSettingsForm from "../polls/PollSettingsForm";

// Types
interface AdvancedSettings {
  showLogo: boolean;
  requireAuth: boolean;
  oneResponsePerPerson: boolean;
  allowEditAfterSubmit: boolean;
  sendEmailCopy: boolean;
  emailForCopy: string;
  sendNotifications: boolean;
  resultsVisibility: string;
  maxResponses?: number;
  expiresAt?: string;
}

interface AdvancedConfigurationPanelProps {
  showAdvanced: boolean;
  advancedSettings: AdvancedSettings;
  onToggleAdvanced: () => void;
  onSettingsChange: (settings: AdvancedSettings) => void;
  pollType: "date" | "form";
  pollError?: string | null;
  emailErrors?: string[];
  className?: string;
  /** Masque la section audience (pilotée par le chip up-front). */
  hideAudience?: boolean;
  /** Débloque la publication sur la page personnelle (SOLO+). */
  hasPaidPlan?: boolean;
}

export const AdvancedConfigurationPanel: React.FC<
  AdvancedConfigurationPanelProps
> = ({
  showAdvanced,
  advancedSettings,
  onToggleAdvanced,
  onSettingsChange,
  pollType,
  pollError,
  emailErrors,
  className = "",
  hideAudience = false,
  hasPaidPlan = false,
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Panneau de configuration avancée */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <button
          onClick={onToggleAdvanced}
          className="w-full flex items-center justify-between p-4 bg-card hover:bg-accent transition-colors"
        >
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">
              Paramètres de configuration
            </span>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-muted-foreground transition-transform ${
              showAdvanced ? "rotate-180" : ""
            }`}
          />
        </button>

        {showAdvanced && (
          <div className="p-4">
            <PollSettingsForm
              settings={advancedSettings}
              onSettingsChange={onSettingsChange}
              pollType={pollType}
              hideAudience={hideAudience}
              hasPaidPlan={hasPaidPlan}
            />
          </div>
        )}
      </div>

      {/* Affichage des erreurs */}
      {pollError && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">Erreur</span>
          </div>
          <p className="text-sm text-destructive mt-1 break-words">
            {pollError}
          </p>
        </div>
      )}

      {/* Affichage des erreurs d'email */}
      {emailErrors && emailErrors.length > 0 && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">Emails invalides</span>
          </div>
          <ul className="text-sm text-destructive mt-1 list-disc list-inside break-words">
            {emailErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
